import {
  AgxAgentAlert,
  AgxDiagonalUpArrowIcon,
  AgxHeader,
  EnvelopeSubmissionType,
  PropertyAddress,
  cleanFullAddress,
  formatCurrency,
} from '@urbanx/agx-ui-components';
import {
  AgxColumn,
  AgxRow,
  AgxDivider,
  AgxTypography,
  AgxButton,
  Icon_Arrow,
  AgxEmptyState,
} from '@urbanx/agx.ui-components2';
import { MenuTitles, Pages } from 'constants/menu';
import { ContentLayout, PageLayout } from 'layout';
import { useContext } from 'react';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { AuthContext } from '@urbanx/agx.auth.client';
import './CampaignMarketingDetails.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { openFileInNewTab, openInNewTab } from 'utils/openInNewTab';
import { useStaffGetMarketingPackage } from '@urbanx/agx.marketing.client';
import CampaignFlowStatusBadge from './CampaignFlowStatusBadge';

const CampaignMarketingDetails = () => {
  const { marketingPackageId } = useParams();
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const { data: marketingPackage, isPending: marketingPackageIsPending } =
    useStaffGetMarketingPackage(marketingPackageId || '', !!marketingPackageId);
  const navigate = useNavigate();

  const marketingPackageTotal =
    (marketingPackage?.marketingContributions?.vendorUpfrontPayment ?? 0) +
    (marketingPackage?.marketingContributions?.agentContributionNoReimbursement ?? 0) +
    (marketingPackage?.marketingContributions?.agentContributionToBeReimbursed ?? 0);
  const marketingPackageDocumentPath = marketingPackage?.document?.containerFilePath;

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CAMPAIGN_MARKETING}
        isCampaigns={true}
      >
        <>
          {marketingPackageIsPending && <div>Loading...</div>}
          {!marketingPackageIsPending && !marketingPackage && (
            <div>No marketing information found for campaign</div>
          )}
          {!marketingPackageIsPending && marketingPackage && (
            <>
              <AgxColumn gap="spacing-xl" fill>
                <AgxRow justifyContent="space-between" alignItems="alignCenter" fill>
                  <AgxColumn>
                    <AgxButton
                      id={'btnBack'}
                      onClick={() =>
                        navigate(
                          `/${Pages.AGENCIES}/${agency?.id}/campaigns/${campaign?.campaignId}/campaign-marketing`
                        )
                      }
                      text={
                        <AgxTypography id={'btnBackText'} type={'body_ui'} size={'small'}>
                          Marketing
                        </AgxTypography>
                      }
                      variant={'naked'}
                      leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
                    />
                  </AgxColumn>
                  <AgxRow gap="spacing-lg">
                    <AgxButton
                      id="mktDownloadBtnAction"
                      variant="dark"
                      size="medium"
                      disabled={!marketingPackageDocumentPath}
                      onClick={() => openFileInNewTab(getAuthToken, marketingPackageDocumentPath)}
                      text="Download"
                    />
                  </AgxRow>
                </AgxRow>
                <AgxDivider variant={'horizontal'} />
              </AgxColumn>
              <AgxColumn gap="spacing-md" fill padding={{ top: 'spacing-3xl' }}>
                {(marketingPackage.campaignFlowErrors?.length ?? 0) > 0 && (
                  <AgxAgentAlert
                    heading="Campaign Flow Export Errors:"
                    extraClasses="marketing-alert"
                    body={marketingPackage.campaignFlowErrors?.join(', ') || ''}
                  />
                )}
              </AgxColumn>
              <AgxRow id="marketing-panels" fill>
                <AgxRow gap="spacing-3xl" justifyContent="left" fill>
                  <AgxColumn id="marketing-column-cards">
                    <AgxColumn gap="spacing-lg" id="marketing-summary-card">
                      <div className="panel">
                        <AgxColumn gap="spacing-lg">
                          <AgxColumn gap="spacing-3xl" fill padding={{ left: 'spacing-md' }}>
                            {marketingPackage.campaignFlowUrl &&
                              marketingPackage.campaignFlowCampaignId && (
                                <AgxButton
                                  id={'btnBack'}
                                  onClick={() => {
                                    openInNewTab(marketingPackage.campaignFlowUrl);
                                  }}
                                  text={
                                    <AgxTypography
                                      id={'btnBackText'}
                                      type={'body_ui'}
                                      size={'small'}
                                      color="#318CE0"
                                    >
                                      {marketingPackage.campaignFlowCampaignId}
                                    </AgxTypography>
                                  }
                                  variant={'naked'}
                                  size="small"
                                  rightIcon={
                                    <AgxDiagonalUpArrowIcon
                                      color="#318CE0"
                                      width={16}
                                      height={16}
                                    />
                                  }
                                />
                              )}
                            <AgxHeader size={3}>{marketingPackage.packageTemplateName}</AgxHeader>
                            <CampaignFlowStatusBadge
                              status={marketingPackage.campaignFlowDisplayStatus}
                            />
                            <AgxTypography
                              id={'btnBackText'}
                              type={'body_ui'}
                              size={'small'}
                              color={'var(--text-tertiary)'}
                            >
                              {marketingPackage.campaignFlowStatusInfo}
                            </AgxTypography>
                          </AgxColumn>
                          <AgxColumn gap="spacing-md" fill>
                            <table style={{ width: '100%' }}>
                              <tbody>
                                {marketingPackage.marketingContributions?.vendorUpfrontPayment ? (
                                  <tr>
                                    <td>Vendor pay upfront</td>
                                    <td align="right">
                                      <AgxTypography
                                        id={'marketingPackageVendorUpfrontPayment'}
                                        type="body_ui_strong"
                                        size={'small'}
                                      >
                                        {formatCurrency(
                                          marketingPackage.marketingContributions
                                            ?.vendorUpfrontPayment
                                        )}
                                      </AgxTypography>
                                    </td>
                                  </tr>
                                ) : undefined}
                                {marketingPackage.marketingContributions
                                  ?.agentContributionNoReimbursement ? (
                                  <tr>
                                    <td>Agent contribution (no reimbursement)</td>
                                    <td align="right">
                                      <AgxTypography
                                        id={'marketingPackageAgentContributionNoReimbursement'}
                                        type="body_ui_strong"
                                        size={'small'}
                                      >
                                        {formatCurrency(
                                          marketingPackage.marketingContributions
                                            ?.agentContributionNoReimbursement
                                        )}
                                      </AgxTypography>
                                    </td>
                                  </tr>
                                ) : undefined}
                                {marketingPackage.marketingContributions
                                  ?.agentContributionToBeReimbursed ? (
                                  <tr>
                                    <td>Agent contribution (to be reimbursed)</td>
                                    <td align="right">
                                      <AgxTypography
                                        id={'marketingPackageAgentContributionToBeReimbursed'}
                                        type="body_ui_strong"
                                        size={'small'}
                                      >
                                        {formatCurrency(
                                          marketingPackage.marketingContributions
                                            ?.agentContributionToBeReimbursed
                                        )}
                                      </AgxTypography>
                                    </td>
                                  </tr>
                                ) : undefined}
                                <tr>
                                  <td>Total</td>
                                  <td align="right">
                                    <AgxTypography
                                      id={'marketingPackageTotal'}
                                      type="body_ui_strong"
                                      size={'small'}
                                    >
                                      {formatCurrency(marketingPackageTotal)}
                                    </AgxTypography>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </AgxColumn>
                        </AgxColumn>
                      </div>
                    </AgxColumn>
                    <AgxColumn gap="spacing-lg" id="marketing-inclusions-addons-card">
                      <div className="panel fill">
                        <AgxColumn gap="spacing-lg">
                          <AgxTypography id={'panelInclusionsHeading'} type="title" size="large">
                            Inclusions
                          </AgxTypography>
                          <AgxDivider variant="horizontal" />
                          <AgxColumn gap="spacing-md" fill>
                            {(marketingPackage.inclusions?.length ?? 0) > 0 &&
                            marketingPackage.envelopeSubmission?.type ===
                              EnvelopeSubmissionType.DocuSign ? (
                              <table style={{ width: '100%' }}>
                                <tbody>
                                  {marketingPackage.inclusions?.map((inclusion) => (
                                    <tr key={inclusion.itemTemplateId}>
                                      <td>{inclusion.itemTemplateName}</td>
                                      <td align="right">
                                        <AgxTypography
                                          id={`inclusionPrice-${inclusion.itemTemplateId}`}
                                          type="body_ui_strong"
                                          size={'small'}
                                        >
                                          {inclusion.pricePerUnit
                                            ? formatCurrency(inclusion.pricePerUnit)
                                            : 'Complimentary'}
                                        </AgxTypography>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <AgxRow
                                gap="spacing-md"
                                alignItems="alignCenter"
                                justifyContent="center"
                                fill
                                padding={{
                                  top: 'spacing-md',
                                  bottom: 'spacing-md',
                                  left: 'spacing-xl',
                                  right: 'spacing-xl',
                                }}
                              >
                                <AgxEmptyState
                                  variant="minimal"
                                  label="Inclusion list not available. Download to view"
                                />
                              </AgxRow>
                            )}
                          </AgxColumn>
                        </AgxColumn>
                      </div>
                      <div className="panel fill">
                        <AgxColumn gap="spacing-lg">
                          <AgxTypography id={'panelAddonsHeading'} type="title" size="large">
                            Upgrades
                          </AgxTypography>
                          <AgxDivider variant="horizontal" />
                          <AgxColumn gap="spacing-md" fill>
                            {(marketingPackage.addons?.length ?? 0) > 0 &&
                            marketingPackage.envelopeSubmission?.type ===
                              EnvelopeSubmissionType.DocuSign ? (
                              <table style={{ width: '100%' }}>
                                <tbody>
                                  {marketingPackage.addons?.map((addon) => (
                                    <tr key={addon.itemTemplateId}>
                                      <td>{addon.itemTemplateName}</td>
                                      <td align="right">
                                        <AgxTypography
                                          id={`addonPrice-${addon.itemTemplateId}`}
                                          type="body_ui_strong"
                                          size={'small'}
                                        >
                                          {addon.pricePerUnit
                                            ? formatCurrency(addon.pricePerUnit)
                                            : 'Complimentary'}
                                        </AgxTypography>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <AgxRow
                                gap="spacing-md"
                                alignItems="alignCenter"
                                justifyContent="center"
                                fill
                                padding={{
                                  top: 'spacing-md',
                                  bottom: 'spacing-md',
                                  left: 'spacing-xl',
                                  right: 'spacing-xl',
                                }}
                              >
                                <AgxEmptyState
                                  variant="minimal"
                                  label={
                                    marketingPackage.envelopeSubmission?.type ===
                                    EnvelopeSubmissionType.DocuSign
                                      ? 'No upgrades available'
                                      : 'Upgrades not available. Download to view'
                                  }
                                />
                              </AgxRow>
                            )}
                          </AgxColumn>
                        </AgxColumn>
                      </div>
                    </AgxColumn>
                  </AgxColumn>
                </AgxRow>
              </AgxRow>
            </>
          )}
        </>
      </ContentLayout>
    </PageLayout>
  );
};

export default CampaignMarketingDetails;
